'use client';

import { useApi } from '@repo/api';
import { VAT_LOOKUP_PATTERN } from '@repo/schema/vat';
import { getCreditSafeAddressLine, getCreditSafeVatParams } from '@repo/sdk/helpers';
import { useMutation, useQuery } from '@repo/tanstack/query';
import { useRef, useState } from 'react';
type SetFieldValue = (field: 'address.address' | 'address.city' | 'address.postcode' | 'country' | 'phone' | 'phone_country' | 'registration_number' | 'title' | 'vat', value: string) => void;
type UseCreateCompanyVatParams = {
  country: string;
  setFieldValue: SetFieldValue;
  vat: string;
};
export const useCreateCompanyVat = ({
  country,
  setFieldValue,
  vat
}: UseCreateCompanyVatParams) => {
  const api = useApi();
  const [vatLookupMessage, setVatLookupMessage] = useState<null | string>(null);
  const lastLookedUpVatRef = useRef(vat.replaceAll(' ', '').toUpperCase());
  const normalizedVat = vat.replaceAll(' ', '').toUpperCase();
  const isVatNumberValid = VAT_LOOKUP_PATTERN.test(normalizedVat);
  const existingCompanyQuery = useQuery({
    ...api.companies.list.queryOptions({
      input: {
        'filter[vat]': normalizedVat
      }
    }),
    enabled: isVatNumberValid
  });
  const {
    isPending: isVatLookupPending,
    mutateAsync: lookupCompanyByVat
  } = useMutation({
    mutationFn: async (vatNumber: string) => api.creditSafe.companies.query({
      input: {
        country,
        vat: vatNumber
      }
    })
  });
  const handleVatBlur = async (currentVat: string) => {
    const nextVat = currentVat.replaceAll(' ', '').toUpperCase();
    setFieldValue('vat', nextVat);
    if (!VAT_LOOKUP_PATTERN.test(nextVat)) {
      setVatLookupMessage(null);
      return;
    }
    if (nextVat === lastLookedUpVatRef.current) return;
    setVatLookupMessage(null);
    try {
      const response = await lookupCompanyByVat(nextVat);
      const company = response.companies[0];
      lastLookedUpVatRef.current = nextVat;
      if (!company) {
        setVatLookupMessage('No company details found in Creditsafe. Please complete the form manually.');
        return;
      }
      const {
        countries
      } = getCreditSafeVatParams(nextVat, country);
      setFieldValue('address.address', getCreditSafeAddressLine(company.address));
      setFieldValue('address.city', company.address.city ?? '');
      setFieldValue('address.postcode', company.address.postCode ?? '');
      setFieldValue('country', company.country ?? '');
      setFieldValue('phone', company.phoneNumbers?.[0] ?? '');
      setFieldValue('phone_country', company.country ?? countries);
      setFieldValue('registration_number', company.regNo ?? '');
      setFieldValue('title', company.name);
      setVatLookupMessage(null);
    } catch {
      setVatLookupMessage('VAT lookup is unavailable right now. Please complete the form manually.');
    }
  };
  return {
    existingCompany: isVatNumberValid ? existingCompanyQuery.data?.data[0] : undefined,
    handleVatBlur,
    isVatLookupPending,
    vatLookupMessage
  };
};